/**
 * CashDropDialog — Phase 4.
 *
 * Mid-shift cash drop UI. Triggered from the ActiveShiftBadge's "Drop"
 * action. Cashier specifies amount + destination (safe / main_drawer),
 * manager approves with PIN. Server validates the sub-drawer has enough
 * cash and writes the ledger movements + appends to shift.drops.
 */
import { useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Banknote, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function CashDropDialog({ open, onOpenChange, shiftId, onDropped }) {
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState('safe');
  const [note, setNote] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setAmount('');
      setDestination('safe');
      setNote('');
      setManagerPin('');
    }
  }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error('Amount must be greater than zero.');
      return;
    }
    if (!managerPin.trim()) {
      toast.error('Manager PIN is required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/shifts/${shiftId}/drop`, {
        amount: amt,
        destination,
        manager_pin: managerPin.trim(),
        note: note.trim() || undefined,
      });
      toast.success(`Drop of ₱${amt.toLocaleString()} to ${destination.replace('_', ' ')} recorded.`);
      onDropped?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Drop failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cash-drop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote size={18} className="text-emerald-700" />
            Cash Drop
          </DialogTitle>
          <DialogDescription>
            Reduce the cash you're accountable for by moving it to the safe or main drawer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="drop-amount">Amount (₱)</Label>
            <Input
              id="drop-amount"
              data-testid="drop-amount-input"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Destination</Label>
            <RadioGroup value={destination} onValueChange={setDestination}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="safe" id="dest-safe" data-testid="dest-safe" />
                <Label htmlFor="dest-safe" className="font-normal cursor-pointer">Safe (locked drawer)</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="main_drawer" id="dest-main" data-testid="dest-main" />
                <Label htmlFor="dest-main" className="font-normal cursor-pointer">Main Drawer</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="drop-note">Note (optional)</Label>
            <Input
              id="drop-note"
              data-testid="drop-note-input"
              placeholder="e.g., envelope #4, witnessed by manager"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="drop-manager-pin" className="flex items-center gap-1.5">
              <KeyRound size={13} /> Manager PIN
            </Label>
            <Input
              id="drop-manager-pin"
              data-testid="drop-manager-pin-input"
              type="password"
              placeholder="Required"
              value={managerPin}
              onChange={(e) => setManagerPin(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" data-testid="cancel-drop" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            data-testid="confirm-drop"
            onClick={submit}
            disabled={submitting || !amount || !managerPin.trim()}
            className="bg-emerald-700 hover:bg-emerald-800"
          >
            {submitting ? 'Recording…' : 'Record Drop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
